import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { AppShell } from "../components/AppShell";
import { LineField } from "../components/LineField";
import { useT } from "../i18n/LanguageProvider";
import { useChangePassword, AuthErrorCode } from "../hooks/useAuthOperations";
import { PASSWORD_POLICY } from "../utils/passwordPolicy";
import { C, font, radii, shadows, space, type } from "../styles/tokens";
import { BrandButton } from "../components/brand";

/**
 * ChangePasswordPage - change the signed-in user's password.
 *
 * Accessible to any signed-in role (Professional, officer, admin).
 * Add a link to this page from wherever "Account settings" lives.
 *
 * Security model:
 *   - Requires current password (reauthentication) before allowing the change.
 *     This protects against someone walking up to an unlocked screen.
 *   - POST /api/auth/change-password checks the current password server-side,
 *     rate limits attempts, and signs out every other session.
 *   - New password follows the server policy: at least 8 characters with an
 *     uppercase letter, a lowercase letter and a number. It is checked here
 *     first only so the person gets immediate feedback.
 *   - Confirm password field prevents typos.
 */

export function ChangePasswordPage() {
  const t = useT();
  const navigate = useNavigate();
  const { changePassword, busy, error, success } = useChangePassword();

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [validationError, setValidationError] = useState(null);

  async function handleSubmit(e) {
    e.preventDefault();
    setValidationError(null);

    if (!PASSWORD_POLICY.test(newPassword)) {
      setValidationError(
        "New password needs at least 8 characters, including an uppercase letter, a lowercase letter and a number.",
      );
      return;
    }
    if (newPassword !== confirmPassword) {
      setValidationError("New passwords don't match.");
      return;
    }
    if (newPassword === currentPassword) {
      setValidationError(
        "New password must be different from your current password.",
      );
      return;
    }

    await changePassword(currentPassword, newPassword);
  }

  const [hoverCancel, setHoverCancel] = useState(false);
  const [hoverSubmit, setHoverSubmit] = useState(false);

  // Which field to highlight based on error code
  const highlightCurrentPw = error?.code === AuthErrorCode.WRONG_PASSWORD;

  return (
    <AppShell maxWidth={520}>
      <div style={{ marginBottom: 20 }}>
        <button onClick={() => navigate(-1)} style={backButtonStyle}>
          Back
        </button>
      </div>

      <div style={cardStyle}>
        <div style={{ marginBottom: 24 }}>
          <h1
            style={{
              fontSize: 22,
              fontWeight: 700,
              margin: "0 0 4px",
              letterSpacing: -0.3,
            }}
          >
            Change password
          </h1>
          <p
            style={{
              fontSize: 14,
              color: C.textMed,
              margin: 0,
              lineHeight: 1.5,
            }}
          >
            You'll need your current password to make this change.
          </p>
        </div>

        {success ? (
          <SuccessState onDone={() => navigate(-1)} />
        ) : (
          <form onSubmit={handleSubmit}>
            {/* Current password */}
            <div style={{ marginBottom: 16 }}>
              <LineField
                label="Current password"
                type="password"
                placeholder="Your current password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
              {highlightCurrentPw && (
                <div style={fieldErrorStyle}>{error.message}</div>
              )}
            </div>

            {/* New password */}
            <LineField
              label="New password"
              type="password"
              placeholder="8+ characters, with A-Z, a-z and 0-9"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoComplete="new-password"
              minLength={8}
              required
            />

            {/* Confirm new password */}
            <LineField
              label="Confirm new password"
              type="password"
              placeholder="Repeat new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
              required
            />

            {/* Password strength hint */}
            {newPassword.length > 0 && (
              <PasswordStrengthBar password={newPassword} />
            )}

            {/* Validation errors (client-side) */}
            {validationError && (
              <div style={errorBoxStyle}>{validationError}</div>
            )}

            {/* Provider errors (server-side, non-field-specific) */}
            {error && !highlightCurrentPw && (
              <div style={errorBoxStyle}>{error.message}</div>
            )}

            <div style={{ display: "flex", gap: space.sm, marginTop: space.xl }}>
              <BrandButton
                type="button"
                variant="secondary"
                onClick={() => navigate(-1)}
              >
                {t("common.cancel")}
              </BrandButton>
              <BrandButton
                type="submit"
                variant="primary"
                loading={busy}
                fullWidth
              >
                {busy ? "Updating..." : "Update password"}
              </BrandButton>
            </div>

            <p
              style={{
                fontSize: 12,
                color: C.textSubtle,
                textAlign: "center",
                marginTop: space.md,
              }}
            >
              Forgot your current password?{" "}
              <a
                href="/forgot-password"
                style={{
                  color: C.primary,
                  textDecoration: "none",
                  fontWeight: 600,
                }}
              >
                Reset it here
              </a>
            </p>
          </form>
        )}
      </div>
    </AppShell>
  );
}

// ---- subcomponents ----

function SuccessState({ onDone }) {
  return (
    <div style={{ textAlign: "center", padding: "8px 0 16px" }}>
      <div
        style={{
          width: 56,
          height: 56,
          borderRadius: "50%",
          background: C.greenLight,
          color: C.green,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          margin: "0 auto 16px",
        }}
      >
        <svg
          width="28"
          height="28"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
      </div>
      <h2 style={{ fontSize: 20, fontWeight: 700, margin: "0 0 8px" }}>
        Password updated
      </h2>
      <p
        style={{
          fontSize: 14,
          color: C.textMed,
          margin: "0 0 24px",
          lineHeight: 1.5,
        }}
      >
        Your password has been changed successfully. Use the new password next
        time you sign in.
      </p>
      <button
        onClick={onDone}
        style={{
          padding: "12px 28px",
          fontSize: 14,
          fontWeight: 700,
          color: "white",
          background: "#472BE4",
          border: 0,
          borderRadius: 10,
          cursor: "pointer",
          fontFamily: font,
          boxShadow: "0 4px 14px rgba(71, 43, 228, 0.28)",
        }}
      >
        Done
      </button>
    </div>
  );
}

function PasswordStrengthBar({ password }) {
  const strength = getStrength(password);
  const widths = { weak: "33%", fair: "66%", strong: "100%" };
  const colors = { weak: C.red, fair: C.amber, strong: C.green };
  const labels = { weak: "Weak", fair: "Fair", strong: "Strong" };

  return (
    <div style={{ marginTop: -4, marginBottom: 12 }}>
      <div
        style={{
          height: 4,
          background: C.divider,
          borderRadius: 2,
          overflow: "hidden",
          marginBottom: 4,
        }}
      >
        <div
          style={{
            height: "100%",
            width: widths[strength],
            background: colors[strength],
            borderRadius: 2,
            transition: "width 0.2s ease, background 0.2s ease",
          }}
        />
      </div>
      <div style={{ fontSize: 11, color: colors[strength], fontWeight: 600 }}>
        {labels[strength]}
      </div>
    </div>
  );
}

function getStrength(password) {
  let score = 0;
  if (password.length >= 8) score++;
  if (password.length >= 12) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;
  if (score <= 2) return "weak";
  if (score <= 3) return "fair";
  return "strong";
}

// ---- styles ----

const cardStyle = {
  backgroundColor: C.surface,
  borderRadius: radii.card,
  padding: "36px 32px",
  boxShadow: shadows.card,
};

const backButtonStyle = {
  background: "transparent",
  border: 0,
  color: C.primary,
  fontSize: 13.5,
  fontWeight: 700,
  cursor: "pointer",
  padding: "6px 12px",
  borderRadius: radii.control,
  fontFamily: font,
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  transition: "all 0.15s ease",
};

const fieldErrorStyle = {
  fontSize: 12,
  color: C.danger,
  marginTop: 4,
};

const errorBoxStyle = {
  fontSize: 13,
  color: C.danger,
  background: C.dangerTint,
  padding: "10px 12px",
  borderRadius: radii.control,
  marginTop: 8,
};
